<template>
  <IvrLayout title="電話自動受付予約（IVR）設定" showBack :onHome="back" :loading="busy">
    <ConfigPage title="予約受付パターン" :gap="24" scrollable>
      <template slot="header">
        <Button flat @click="onEdit"><AddStrongIcon />新規</Button>
      </template>
      <p>
        電話自動受付予約（IVR）の受付パターンを設定してください。<br />
        期間・日付指定と曜日指定の設定が重複している場合、期間・日付指定の設定が優先されます。
      </p>
      <section>
        <div class="section-title">
          <p class="title">期間・日付指定</p>
          <Button class="button" @click="toOrder('term')" :disabled="hasTermList < 2">
            <MoveIcon /><span>期間・日付指定優先度</span>
          </Button>
        </div>
        <DataTable
          :data="ivrTermList.terms"
          :minItemHeight="58"
          :columns="[
            { key: 'active', name: '設定' },
            { key: 'displayOrder', name: '優先' },
            { key: 'name', name: 'パターン名' },
            { key: 'date', name: '期間・日付' },
            { key: 'timeFrame', name: '時間' },
          ]"
          editButton
          @edit="onEdit($event, 'term')"
          deleteButton
          @delete="onDelete($event, 'term')"
        >
          <template #active="{ item }">
            {{ item.active ? '有効' : '無効' }}
          </template>
          <template #date="{ item }">
            {{ item.startDate.replace(/-/g, '/') }}〜{{ item.endDate.replace(/-/g, '/') }}
          </template>
          <template #timeFrame="{ item }">
            <p v-for="(timeFrame, key) in item.timeFrame" :key="key">
              {{ timeFrame.startTime }}〜{{ timeFrame.endTime }}
            </p>
          </template>
        </DataTable>
      </section>

      <section>
        <div class="section-title">
          <p class="title">曜日指定</p>
          <Button class="button" @click="toOrder('week')" :disabled="hasWeekList < 2">
            <MoveIcon /><span>曜日指定優先度</span>
          </Button>
        </div>
        <DataTable
          :data="ivrWeekList.weeks"
          :minItemHeight="58"
          :columns="[
            { key: 'active', name: '設定' },
            { key: 'displayOrder', name: '優先' },
            { key: 'name', name: 'パターン名' },
            { key: 'date', name: '曜日' },
            { key: 'timeFrame', name: '時間' },
          ]"
          editButton
          @edit="onEdit($event, 'week')"
          deleteButton
          @delete="onDelete($event, 'week')"
        >
          <template #active="{ item }">
            {{ item.active ? '有効' : '無効' }}
          </template>
          <template #date="{ item }">
            <p>{{ weeksText(item) }}</p>
          </template>
          <template #timeFrame="{ item }">
            <p v-for="(timeFrame, key) in item.timeFrame" :key="key">
              {{ timeFrame.startTime }}〜{{ timeFrame.endTime }}
            </p>
          </template>
        </DataTable>
      </section>
    </ConfigPage>
  </IvrLayout>
</template>

<script>
import { mapState } from 'vuex'
import IvrLayout from '@/components/templates/IvrLayout'
import ConfigPage from '@/components/templates/ConfigPage'
import Button from '@/components/atoms/Button'
import DataTable from '@/components/molecules/DataTable'
import Dialog from '@/components/molecules/ModalDialog'
import AddStrongIcon from '@/components/icons/addStrong'
import MoveIcon from '@/components/icons/move'

export default {
  name: 'IvrTime',
  components: {
    IvrLayout,
    ConfigPage,
    Button,
    DataTable,
    AddStrongIcon,
    MoveIcon,
  },
  data() {
    return {
      STATIC_PATH,
      GNAVI_MAINTENANCE,
    }
  },
  computed: {
    ...mapState({
      ivrTermList: state => state.ivr.list.ivrTermList,
      ivrWeekList: state => state.ivr.list.ivrWeekList,
      hasTermList: state => state.ivr.list.ivrTermList?.terms?.length || 0,
      hasWeekList: state => state.ivr.list.ivrWeekList?.weeks?.length || 0,
      busy: state => state.ivr.list.busy,
    }),
  },
  async created() {
    await this.fetch(true)
  },
  methods: {
    async fetch(readonly) {
      const { dispatch } = this.$store
      await dispatch('ivr/list/getTerms', { readonly })
      await dispatch('ivr/list/getWeeks', { readonly })
      // 優先度順
      this.ivrTermList.terms.sort((a, b) => {
        return a.displayOrder - b.displayOrder
      })
      // 優先度順
      this.ivrWeekList.weeks.sort((a, b) => {
        return a.displayOrder - b.displayOrder
      })
    },
    weeksText(data) {
      const weekObj = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'holiday'].map(week => ({
        weekKey: week,
        label: {
          mon: '月',
          tue: '火',
          wed: '水',
          thu: '木',
          fri: '金',
          sat: '土',
          sun: '日',
          holiday: '祝日',
        }[week],
        weekValue: data[week],
      }))
      return (
        weekObj
          .map(week => week.weekValue && week.label)
          .filter(v => v)
          .join('・') || '未設定'
      )
    },
    toOrder(type) {
      this.$router.push({ name: 'IvrOrder', query: { type } })
    },
    onEdit({ id }, type) {
      this.$router.push({ name: 'IvrEdit', query: { id, type } })
    },
    async onDelete(event, type) {
      if (await this.deleteConfirm(event.name)) {
        if (!(await this.$store.dispatch('ivr/edit/delete', { id: event.id }))) return
        // 削除したリストを除外
        const deletedList =
          type === 'term'
            ? this.ivrTermList.terms.filter(item => item.id !== event.id)
            : this.ivrWeekList.weeks.filter(item => item.id !== event.id)
        // 優先順位を新たにセット
        deletedList.map((val, index) => (val.displayOrder = index + 1))
        type === 'term'
          ? await this.$store.dispatch('ivr/edit/saveTerms', { terms: deletedList })
          : await this.$store.dispatch('ivr/edit/saveWeeks', { weeks: deletedList })
        this.fetch(false)
      }
    },
    deleteConfirm(name) {
      return Dialog.confirm({
        title: '予約受付パターン',
        message: '以下の予約受付パターンを削除してもよろしいですか？',
        target: name,
        okText: '削除',
        cancelText: '戻る',
        caution: true,
      })
    },
    back() {
      this.$router.go(-1)
    },
  },
}
</script>

<style lang="postcss" scoped>
.title {
  font-size: 15px;
  font-weight: bold;
  padding-bottom: 16px;
}

.section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;

  .title {
    padding-bottom: 0;
  }

  .button {
    align-items: center;
    border-radius: 4px;
    box-shadow: none;
    display: flex;
    font-size: 13px;
    height: 32px;
    justify-content: center;
    padding: 0 14px;
    width: auto;

    > span {
      padding-left: 8px;
    }
  }
}

>>> table {
  min-height: auto;
}

>>> td {
  padding: 12px 16px;
}

>>> .active,
>>> .displayOrder {
  justify-content: center;
  min-width: 60px;
  padding: 0;
  width: 60px;
}

>>> .name {
  min-width: 338px;
}

>>> .date {
  justify-content: center;
  min-width: 280px;
  width: 280px;
}

>>> .timeFrame {
  justify-content: center;
  min-width: 140px;
  width: 140px;
}

>>> td.timeFrame {
  flex-flow: column;
}
</style>
